import { inject, Injectable } from '@angular/core';
import {
  type CreateLearningResourceMicroLearningUserProgressInput,
  type CreateMicroLearningUserProgressMutation,
  type CreateMicroLearningUserProgressMutationVariables,
  MicroLearningUserProgressStatusEnum,
  type UpdateLearningResourceMicroLearningUserProgressInput,
  type UpdateMicroLearningUserProgressMutation,
  type UpdateMicroLearningUserProgressMutationVariables,
  type UserWithMicroLearningProgressesQuery,
  type UserWithMicroLearningProgressesQueryVariables,
  type WatchViewerWithMicroLearningProgressesQuery,
  type WatchViewerWithMicroLearningProgressesQueryVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { orderBy } from 'lodash-es';
import { EMPTY, map, of, switchMap, take } from 'rxjs';
import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { isNotNull } from '../../../utils/nullable.utils';
import { assertGraphqlDataNode } from '../../../utils/type-helpers';
import {
  type LearningResourceMicroLearningId,
  type LearningResourceMicroLearningStepId,
  type UserId,
} from '../../../utils/type-helpers-ids.generated';

const microLearningUserProgressFragment = gql`
  fragment MicroLearningUserProgress on LearningResourceMicroLearningUserProgress {
    id
    currentMicroLearningStep {
      id
    }
    microLearning {
      id
    }
    status
  }
`;

const userWithMicroLearningProgressesQuery = gql<
  UserWithMicroLearningProgressesQuery,
  UserWithMicroLearningProgressesQueryVariables
>`
  query UserWithMicroLearningProgresses($userId: ID!) {
    node(id: $userId) {
      ... on User {
        id
        microLearningUserProgresses {
          nodes {
            ...MicroLearningUserProgress
          }
        }
      }
    }
  }
  ${microLearningUserProgressFragment}
`;

@Injectable({ providedIn: 'root' })
export class SharedLearningResourceMicrolearningProgressApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchMicroLearningProgressesForUserId(variables: UserWithMicroLearningProgressesQueryVariables) {
    return this.sharedGraphQlService
      .watchQuery({
        query: userWithMicroLearningProgressesQuery,
        variables,
      })
      .pipe(map((res) => assertGraphqlDataNode(res, 'User').microLearningUserProgresses.nodes ?? []));
  }

  watchMicroLearningProgressForUserAndMicroLearningId(microLearning: LearningResourceMicroLearningId, user: UserId) {
    return this.watchMicroLearningProgressesForUserId({ userId: user.id }).pipe(
      // return null instead of undefined as undefined keeps resources in loading state.
      map((res) => res.find((ml) => ml.microLearning?.id === microLearning.id) ?? null),
    );
  }

  watchViewerWithMicroLearningProgress() {
    return this.sharedGraphQlService
      .watchQuery<WatchViewerWithMicroLearningProgressesQuery, WatchViewerWithMicroLearningProgressesQueryVariables>({
        query: gql`
          query WatchViewerWithMicroLearningProgresses {
            viewer {
              id
              microLearningUserProgresses {
                nodes {
                  id
                  currentMicroLearningStep {
                    id
                  }
                  microLearning {
                    id
                    microLearningSteps {
                      nodes {
                        id
                        order
                      }
                    }
                  }
                  status
                }
              }
            }
          }
        `,
      })
      .pipe(map((result) => result.data.viewer));
  }

  watchViewerMicroLearningProgressForMicroLearning(microLearning: LearningResourceMicroLearningId) {
    return this.watchViewerWithMicroLearningProgress().pipe(
      map(
        (
          progresses,
        ): {
          status: MicroLearningUserProgressStatusEnum;
          percent: number;
        } => {
          const progressForMicroLearning = progresses?.microLearningUserProgresses.nodes?.find(
            (mlup) => mlup?.microLearning?.id === microLearning.id,
          );

          if (
            !progressForMicroLearning ||
            progressForMicroLearning.status === MicroLearningUserProgressStatusEnum.NOT_STARTED
          ) {
            return { percent: 0, status: MicroLearningUserProgressStatusEnum.IN_PROGRESS };
          }

          if (progressForMicroLearning.status === MicroLearningUserProgressStatusEnum.COMPLETED) {
            return { percent: 100, status: MicroLearningUserProgressStatusEnum.COMPLETED };
          }

          const orderedStepIds = orderBy(
            progressForMicroLearning.microLearning?.microLearningSteps?.nodes?.filter(isNotNull) ?? [],
            (step) => step.order,
          ).map((step) => step.id);
          const currentStepId = progressForMicroLearning.currentMicroLearningStep?.id;
          const currentStepIndex = currentStepId ? orderedStepIds.indexOf(currentStepId) : -1;

          return {
            percent: ((currentStepIndex + 1) / orderedStepIds.length) * 100,
            status: MicroLearningUserProgressStatusEnum.IN_PROGRESS,
          };
        },
      ),
    );
  }

  obtainMicroLearningProgress(microLearning: LearningResourceMicroLearningId, user: UserId) {
    return this.watchMicroLearningProgressForUserAndMicroLearningId(microLearning, user).pipe(
      take(1),
      switchMap((progress) =>
        // create progress when one does not exist for this microlearningId
        progress
          ? of(progress)
          : this.createMicroLearningUserProgress({
              microLearningId: microLearning.id,
              userId: user.id,
            }),
      ),
    );
  }

  markMicroLearningAsCompleted(microLearning: LearningResourceMicroLearningId, user: UserId) {
    return this.obtainMicroLearningProgress(microLearning, user).pipe(
      switchMap((progress) =>
        // only mark as complete when the progress status is not already completed
        progress && progress.status !== MicroLearningUserProgressStatusEnum.COMPLETED
          ? this.updateMicroLearningUserProgress({
              learningResourceMicroLearningUserProgressId: progress.id,
              status: MicroLearningUserProgressStatusEnum.COMPLETED,
              // clear currentMicroLearningStep so when you re-open this micro learning after completing,
              // it will open at the first step instead of the last.
              currentMicroLearningStepId: null,
            })
          : EMPTY,
      ),
    );
  }

  setMicroLearningCurrentStep(
    microLearning: LearningResourceMicroLearningId,
    user: UserId,
    microLearningStep: LearningResourceMicroLearningStepId,
  ) {
    return this.obtainMicroLearningProgress(microLearning, user).pipe(
      switchMap((progress) =>
        // only set current step when the progress status is not completed
        progress && progress.status !== MicroLearningUserProgressStatusEnum.COMPLETED
          ? this.updateMicroLearningUserProgress({
              learningResourceMicroLearningUserProgressId: progress.id,
              currentMicroLearningStepId: microLearningStep.id,
              // also set status to IN_PROGRESS as that's always the case when the selected step is changed
              status: MicroLearningUserProgressStatusEnum.IN_PROGRESS,
            })
          : EMPTY,
      ),
    );
  }

  private updateMicroLearningUserProgress(input: UpdateLearningResourceMicroLearningUserProgressInput) {
    return this.sharedGraphQlService.mutateV2<
      UpdateMicroLearningUserProgressMutation,
      UpdateMicroLearningUserProgressMutationVariables
    >({
      mutation: gql`
        mutation UpdateMicroLearningUserProgress($input: UpdateLearningResourceMicroLearningUserProgressInput!) {
          updateLearningResourceMicroLearningUserProgress(input: $input) {
            learningResourceMicroLearningUserProgress {
              ...MicroLearningUserProgress
            }
            errors {
              message
              path
            }
          }
        }
        ${microLearningUserProgressFragment}
      `,
      variables: { input },
    });
  }

  private createMicroLearningUserProgress(input: CreateLearningResourceMicroLearningUserProgressInput) {
    return this.sharedGraphQlService
      .mutateV2<CreateMicroLearningUserProgressMutation, CreateMicroLearningUserProgressMutationVariables>({
        mutation: gql`
          mutation CreateMicroLearningUserProgress($input: CreateLearningResourceMicroLearningUserProgressInput!) {
            createLearningResourceMicroLearningUserProgress(input: $input) {
              learningResourceMicroLearningUserProgress {
                ...MicroLearningUserProgress
              }
              errors {
                message
                path
              }
            }
          }
          ${microLearningUserProgressFragment}
        `,
        variables: { input },
        refetchQueries: [{ query: userWithMicroLearningProgressesQuery, variables: { userId: input.userId } }],
        // to prevent race conditions for other queries dependent on progresses, await the refetch
        awaitRefetchQueries: true,
      })
      .pipe(
        map(
          (res) => res.data?.createLearningResourceMicroLearningUserProgress?.learningResourceMicroLearningUserProgress,
        ),
      );
  }
}
